
import { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../context/AuthContext';

export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};

export const useAuthExtended = () => {
  const auth = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  if (auth === undefined) {
    throw new Error('useAuthExtended must be used within an AuthProvider');
  }

  // Auto-refresh token logic
  useEffect(() => {
    const checkAuth = async () => {
      try {
        setIsLoading(true);
        // Check if token is valid and refresh if needed
        if (auth.user) {
          // Token refresh logic here
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Authentication error');
      } finally {
        setIsLoading(false);
      }
    };

    // Check auth on mount and periodically
    checkAuth();
    
    // Set up interval to check auth (e.g., every 5 minutes)
    const interval = setInterval(checkAuth, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [auth.user]);

  return {
    ...auth,
    isLoading,
    error,
  };
};

// Utility hooks for specific auth needs
export const useRole = () => {
  const { user } = useAuth();
  return {
    isCustomer: user?.role === 'customer',
    isProvider: user?.role === 'provider',
    isAdmin: user?.role === 'admin',
    isAuthenticated: !!user,
  };
};

export const useAuthGuard = (requiredRole?: 'customer' | 'provider' | 'admin') => {
  const { user, isAuthenticated } = useAuth();
  
  const canAccess = () => {
    if (!isAuthenticated) return false;
    if (!requiredRole) return true;
    return user?.role === requiredRole;
  };

  return {
    canAccess,
    isAuthenticated,
    user,
  };
};